import { useAdOperatorAvailability } from '@backoffice-frontend/ad-operator-availability'
import {
  useErrorHandlingMutation,
  useErrorHandlingQuery,
} from '@backoffice-frontend/data-fetching'
import { FccPhoneType } from '@backoffice-frontend/graphql'
import { PhoneEmergencyIcon } from '@moia-dev/pace-icons'
import {
  ActionItem,
  ActionMenu,
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useActionMenu,
} from '@moia-dev/pace-web'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCallButtonStartHandlingVehicleMutation } from '../CallControls/CallControls.hook'
import { useCall } from '../CallProvider/CallProvider'
import {
  type CallButtonVehicleFragment,
  useFccGetPhoneNumbersQuery,
} from './CallButton.hook'

export const CallButton = ({
  vehicle,
  shouldStartHandlingVehicle = false,
}: {
  vehicle: CallButtonVehicleFragment
  shouldStartHandlingVehicle?: boolean
}) => {
  const { t } = useTranslation()
  const { initiateOutgoingCall, state, hasCallAlert } = useCall()
  const { isActive: isOperatorActive } = useAdOperatorAvailability()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isInitiatingCall, setIsInitiatingCall] = useState(false)
  const actionMenuProps = useActionMenu({
    placement: 'bottom-start',
    isOpen: isMenuOpen,
    setIsOpen: setIsMenuOpen,
  })

  const { data } = useErrorHandlingQuery(useFccGetPhoneNumbersQuery, {
    variables: { vehicleId: vehicle.id },
  })

  const [callButtonStartHandlingVehicleMutation] = useErrorHandlingMutation(
    useCallButtonStartHandlingVehicleMutation,
    {},
    _ => t('adFleetMap_tooltip_vehicleAlreadyBeingHandled_label'),
  )

  const passengerPhoneNumber = data?.fccPhoneNumbers.find(
    phoneNumber => phoneNumber.type === FccPhoneType.Conmod,
  )

  const safetyDriverPhoneNumber = data?.fccPhoneNumbers.find(
    phoneNumber => phoneNumber.type === FccPhoneType.Tablet,
  )

  const availablePhoneTypes = [
    ...(passengerPhoneNumber ? [FccPhoneType.Conmod] : []),
    ...(safetyDriverPhoneNumber ? [FccPhoneType.Tablet] : []),
  ]

  const initiateCall = async (phoneType: FccPhoneType) => {
    if (isInitiatingCall) return
    setIsInitiatingCall(true)

    try {
      if (shouldStartHandlingVehicle) {
        const startHandlingVehicleResult =
          await callButtonStartHandlingVehicleMutation({
            variables: { vehicleId: vehicle.id },
          })
        if (startHandlingVehicleResult.errors) {
          return
        }
      }
      switch (phoneType) {
        case FccPhoneType.Tablet:
          initiateOutgoingCall('safetyDriver')
          break

        case FccPhoneType.Conmod:
          initiateOutgoingCall('passenger')
          break
      }
    } finally {
      setIsInitiatingCall(false)
    }
  }

  const getPhoneTypeLabel = (phoneType: FccPhoneType) => {
    switch (phoneType) {
      case FccPhoneType.Conmod:
        return t('adFleetMap_callComponentCallPassenger_button')
      case FccPhoneType.Tablet:
        return t('adFleetMap_callComponentCallSafetyDriver_button')
    }
  }

  const isCallConnected = state.matches('callInProgress')
  const isCallInitiating =
    state.matches('initatingChimeMeeting') ||
    state.matches('waitingForAlertCreation') ||
    state.matches('waitingForCalleeToJoin') ||
    state.matches('ringing') ||
    state.matches('requestingChimeCredentials') ||
    state.matches('joiningIncomingCall')

  const getTooltipTitle = () => {
    if (isCallConnected)
      return t('adFleetMap_callStatus_callAlreadyInProgress_message')
    if (isCallInitiating || isInitiatingCall)
      return t('adFleetMap_callStatus_callBeingInitiated_message')
    if (availablePhoneTypes.length === 0)
      return t(
        'adFleetMap_tooltip_vehicleDrawerFooterCallButtonVehiclePhoneNumberMissing_label',
      )
    if (hasCallAlert)
      return t('adFleetMap_tooltip_vehicleAlreadyHasCallInProgress_label')
    return null
  }

  const tooltipTitle = getTooltipTitle()

  if (availablePhoneTypes.length <= 1) {
    return (
      <Tooltip disabled={tooltipTitle === null} placement="top">
        <TooltipTrigger>
          <Button
            label={t('adFleetMap_callComponentCallVehicle_button')}
            variant="secondary"
            icon={<PhoneEmergencyIcon />}
            onClick={() => {
              const phoneType = availablePhoneTypes.at(0)
              if (phoneType) {
                initiateCall(phoneType)
              }
            }}
            disabled={
              !isOperatorActive ||
              availablePhoneTypes.length === 0 ||
              hasCallAlert ||
              isCallConnected ||
              isCallInitiating ||
              isInitiatingCall
            }
          />
        </TooltipTrigger>
        <TooltipContent>{tooltipTitle}</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Tooltip disabled={tooltipTitle === null} placement="top">
      <TooltipTrigger>
        <span>
          <Button
            ref={actionMenuProps.refs.setReference}
            label={t('adFleetMap_callComponentCallVehicle_button')}
            variant="secondary"
            icon={<PhoneEmergencyIcon />}
            onClick={() => {
              setIsMenuOpen(!isMenuOpen)
            }}
            hasDropdown
            disabled={
              !isOperatorActive ||
              hasCallAlert ||
              isCallConnected ||
              isCallInitiating ||
              isInitiatingCall
            }
          />
          <ActionMenu {...actionMenuProps}>
            {availablePhoneTypes.map(phoneType => (
              <ActionItem
                key={phoneType}
                label={getPhoneTypeLabel(phoneType)}
                onClick={() => {
                  setIsMenuOpen(false)
                  initiateCall(phoneType)
                }}
              />
            ))}
          </ActionMenu>
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltipTitle}</TooltipContent>
    </Tooltip>
  )
}
